import { useState } from 'react';
import { ChevronUp } from 'lucide-react';
import { Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';
import { maskEmail, maskPhone } from '@/lib/masking';
import { useAppUser } from '@/lib/useAppUser';
import { useUsersGetMe } from '@/lib/endpoints';

/** `June 1st 2024` — month name + ordinal day + year, per the Figma design. */
function fmtMemberSince(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getDate();
  const suffix =
    day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
  const month = d.toLocaleDateString('en-US', { month: 'long' });
  return `${month} ${day}${suffix} ${d.getFullYear()}`;
}

/**
 * Collapsible "My info" card: masked phone + email and the member-since date.
 * Values are display-masked ({@link maskPhone} / {@link maskEmail}); rows with
 * no data are omitted rather than rendered empty. Phone and member-since come
 * from the auth profile (`useAppUser`) — `/users/me` doesn't carry them.
 */
export function MyInfoCard() {
  const [open, setOpen] = useState(true);
  const user = useAppUser();
  const { data: profile, isLoading: profileLoading } = useUsersGetMe();

  const email = profile?.email ?? user?.email;
  const memberSince = user?.createdAt ? fmtMemberSince(user.createdAt) : null;
  const rows: { label: string; value: string }[] = [
    ...(user?.phone ? [{ label: 'Phone number', value: maskPhone(user.phone) }] : []),
    ...(email ? [{ label: 'Email', value: maskEmail(email) }] : []),
    ...(memberSince ? [{ label: 'Member since', value: memberSince }] : []),
  ];

  return (
    <section className="w-full rounded-[12px] border-2 border-input-soft bg-surface">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-5 py-3.5"
      >
        <span className="text-lg font-medium text-ink">My info</span>
        <ChevronUp aria-hidden="true" className={cn('size-5 text-ink transition-transform', !open && 'rotate-180')} />
      </button>

      {open && (
        <dl className="flex flex-col gap-1 px-5 pb-4">
          {rows.map(({ label, value }) => (
            <div key={label} className="flex gap-1.5 text-base leading-relaxed">
              <dt className="font-light text-ink">{label}:</dt>
              <dd className="font-medium text-ink">{value}</dd>
            </div>
          ))}
          {/* While the profile is still loading, hold label/value-shaped rows
              instead of flashing the "No profile details yet." empty state. */}
          {rows.length === 0 &&
            profileLoading &&
            ['w-52', 'w-64', 'w-56'].map((width) => (
              <div key={width} className="flex items-center py-[5px]">
                <Skeleton className={cn('h-4 rounded', width)} />
              </div>
            ))}
          {rows.length === 0 && !profileLoading && <p className="text-sm text-muted">No profile details yet.</p>}
        </dl>
      )}
    </section>
  );
}
